package com.siriusxm.pia

import androidx.compose.runtime.Composable
import com.siriusxm.cmc.CMCContainer
import com.siriusxm.cmc.Notification
import com.siriusxm.cmc.utils.Navigator
import com.siriusxm.cmc.utils.Route
import org.w3c.dom.Element
import kotlin.time.Duration

/**
 * Holds data about the environment in which the application is running.
 */
public interface ApplicationContext : TokenAccess, Navigator, CMCContainer {
    public val configuration: Configuration
//    override val viewer: CMCViewer

    /**
     * Client-side discovery of which named unified-content (AVP) actions the current user's token is
     * allowed (UCA-1091). Use it to *hide* entitlement-gated affordances — never as an authorization
     * boundary; the server independently enforces every gated operation. Fails closed. See
     * [Capabilities] and the [can] convenience.
     */
    public val capabilities: Capabilities

    /**
     * The current path
     */
    public val path: String

    /**
     * The media play
     */
    public val player: MediaPlayer?

    public fun notification(
        type: NotificationType, message: String, description: String? = null,
        autoDismiss: Duration? = null
    )

    public fun notification(notification: Notification)

    /**
     * Remove the notification with the given id, if still visible. Defaults to a no-op so existing
     * contexts keep compiling; a context that doesn't override it will leave the toast on screen.
     */
    public fun dismissNotification(id: String) {}

    public fun showError(message: String, description: String? = null) {
        notification(
            NotificationType.ERROR,
            message, description
        )
    }

    public fun info(message: String, description: String? = null, autoDismiss: Duration? = null) {
        notification(
            NotificationType.INFO,
            message, description, autoDismiss
        )
    }
}

/**
 * Show an error that's come from an exception.
 */
public fun ApplicationContext.showError(message: String, throwable: Throwable) {
    showError(message, throwable.message)
}


/**
 * The type of notification to display
 */
public enum class NotificationType {
    INFO,
    WARNING,
    ERROR
}

/**
 * Defines the interface for a console application.
 */
public interface ConsoleApplication {
    /**
     * Initialize the console application. Until this is called, applications
     * should perform any processing.
     */
    public suspend fun initialize(context: ApplicationContext)

    /**
     * Render the given route.
     */
    @Composable
    public fun route(route: Route)
}

public interface DynamicUser {
    public val id: String
    public val accessToken: String?
    public val idToken: String?
}

public interface DynamicApplicationContext {
    public val user: DynamicUser?
    public val stage: String
    public val path: String

    /** The IANA id of the user-selected time zone (e.g. "America/New_York"), chosen via the global
     *  timezone selector in the CMC header. Mirrors [CMCContainer.timeZoneId] for the UMD contract. */
    public val timeZoneId: String
}

public external interface DynamicApplication {
    public fun init(context: DynamicApplicationContext)
    public fun render(element: Element, path: String)
    public fun stop()
}